import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ExternalLink,
  LayoutTemplate,
  PencilLine,
  Store,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../../../components/Loader";
import DeviceFrame from "../../../components/StoreBuilder/DeviceFrame";
import StoreEditorPanel from "../../../components/StoreBuilder/StoreEditorPanel";
import StorePreview from "../../../components/StoreBuilder/StorePreview";
import UnsavedChangesModal from "../../../components/StoreBuilder/UnsavedChangesModal";
import StoreService from "../../../requests/vendor/StoreService";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import {
  createDefaultStoreConfig,
  getHighlightVideoValidationMessage,
  getPromoCardsValidationMessage,
  normalizeStoreConfig,
} from "../../../utils/storeDefaults";
import Config from "../../../Config";
import Images from "../../../Images";

const serializeConfig = (config) => JSON.stringify(normalizeStoreConfig(config));

const StoreBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.user?.user) || null;
  const vendorId = user?.vendor_id || user?.id;

  const [storeConfig, setStoreConfig] = useState(createDefaultStoreConfig());
  const [savedConfig, setSavedConfig] = useState(createDefaultStoreConfig());
  const [deals, setDeals] = useState([]);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [mobileTab, setMobileTab] = useState("edit");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewScrollToken, setPreviewScrollToken] = useState(0);
  const [storeExistsOnServer, setStoreExistsOnServer] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const pendingLeaveActionRef = useRef(null);
  const skipLeaveGuardRef = useRef(false);
  const previewScrollRef = useRef(null);

  const hasUnsavedChanges = useMemo(
    () => serializeConfig(storeConfig) !== serializeConfig(savedConfig),
    [storeConfig, savedConfig]
  );

  const activeDealCount = useMemo(
    () =>
      deals.filter(
        (deal) =>
          deal.status?.toLowerCase() === "active" ||
          deal.status?.toLowerCase() === "approved"
      ).length,
    [deals]
  );

  const loadStore = useCallback(async () => {
    const response = await StoreService.getStoreSettings(vendorId);
    if (response.success) {
      setStoreConfig(response.data);
      setSavedConfig(response.data);
      setStoreExistsOnServer(Boolean(response.existsOnServer));
    }
  }, [vendorId]);

  const loadDeals = useCallback(async () => {
    try {
      const response = await DealsRequest.getAllDeals();
      if (response.success) {
        setDeals(response.data || []);
      }
    } catch {
      toast.error("Could not load your deals for preview.");
    }
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      setIsLoading(true);
      await Promise.all([loadStore(), loadDeals()]);
      setIsLoading(false);
    };
    bootstrap();
  }, [loadStore, loadDeals]);

  useEffect(() => {
    if (!isLoading) {
      setPreviewScrollToken((token) => token + 1);
    }
  }, [isLoading]);

  useEffect(() => {
    if (mobileTab === "preview") {
      setPreviewScrollToken((token) => token + 1);
    }
  }, [mobileTab]);

  const requestLeave = useCallback((leaveAction) => {
    if (skipLeaveGuardRef.current) {
      skipLeaveGuardRef.current = false;
      leaveAction();
      return;
    }

    if (!hasUnsavedChanges) {
      leaveAction();
      return;
    }

    pendingLeaveActionRef.current = leaveAction;
    setLeaveModalOpen(true);
  }, [hasUnsavedChanges]);

  const handleStayOnPage = useCallback(() => {
    pendingLeaveActionRef.current = null;
    setLeaveModalOpen(false);
  }, []);

  const handleLeaveAnyway = useCallback(() => {
    const action = pendingLeaveActionRef.current;
    pendingLeaveActionRef.current = null;
    setLeaveModalOpen(false);
    skipLeaveGuardRef.current = true;
    action?.();
  }, []);

  const handleSaveAndLeave = useCallback(async () => {
    const action = pendingLeaveActionRef.current;
    pendingLeaveActionRef.current = null;

    const promoError = getPromoCardsValidationMessage(storeConfig.promoCards);
    if (promoError) {
      toast.error(promoError);
      setLeaveModalOpen(false);
      return;
    }

    const videoError = getHighlightVideoValidationMessage(storeConfig.highlightVideo);
    if (videoError) {
      toast.error(videoError);
      setLeaveModalOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      const response = await StoreService.saveStoreSettings(vendorId, storeConfig, {
        existsOnServer: storeExistsOnServer,
      });
      if (response.success) {
        setStoreConfig(response.data);
        setSavedConfig(response.data);
        if (response.source === "api") {
          setStoreExistsOnServer(Boolean(response.existsOnServer));
        }
        toast.success(response.message || "Your store has been saved.");
        setLeaveModalOpen(false);
        skipLeaveGuardRef.current = true;
        action?.();
      } else {
        toast.error(response.message || "Failed to save store content.");
        pendingLeaveActionRef.current = action;
      }
    } catch {
      toast.error("Failed to save store settings.");
      pendingLeaveActionRef.current = action;
    } finally {
      setIsSaving(false);
    }
  }, [storeConfig, storeExistsOnServer, vendorId]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleDocumentClick = (event) => {
      const anchor = event.target.closest("a[href]");
      if (!anchor || anchor.target === "_blank" || anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) {
        return;
      }

      let nextPath = href;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        nextPath = `${url.pathname}${url.search}${url.hash}`;
      } catch {
        if (!href.startsWith("/")) return;
      }

      if (nextPath === location.pathname) return;

      event.preventDefault();
      event.stopPropagation();
      requestLeave(() => navigate(nextPath));
    };

    document.addEventListener("click", handleDocumentClick, true);
    return () => document.removeEventListener("click", handleDocumentClick, true);
  }, [hasUnsavedChanges, location.pathname, navigate, requestLeave]);

  const handleSave = async () => {
    const promoError = getPromoCardsValidationMessage(storeConfig.promoCards);
    if (promoError) {
      toast.error(promoError);
      return;
    }

    const videoError = getHighlightVideoValidationMessage(storeConfig.highlightVideo);
    if (videoError) {
      toast.error(videoError);
      return;
    }

    setIsSaving(true);
    try {
      const response = await StoreService.saveStoreSettings(vendorId, storeConfig, {
        existsOnServer: storeExistsOnServer,
      });
      if (response.success) {
        setStoreConfig(response.data);
        setSavedConfig(response.data);
        if (response.source === "api") {
          setStoreExistsOnServer(Boolean(response.existsOnServer));
        }
        toast.success(
          response.message ||
            (response.source === "api"
              ? "Your store has been saved."
              : "Store saved offline. Will sync when you're back online.")
        );
        setPreviewScrollToken((token) => token + 1);
      } else {
        toast.error(response.message || "Failed to save store content.");
      }
    } catch {
      toast.error("Failed to save store settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBackToDashboard = () => {
    requestLeave(() => navigate("/dashboard"));
  };

  const storeUrl = user?.vendor_slug
    ? `${Config.consumerDomain}/store/${user.vendor_slug}`
    : null;

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-[#FAFAFA]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden bg-[#FAFAFA]">
      {/* Workspace top bar */}
      <header className="shrink-0 z-30 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 px-4 h-14 sm:px-6">
          <img src={Images.shortlogo} alt="Dealboxx" className="w-7 h-7 shrink-0" />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-bold text-gray-900 truncate sm:text-base">
                Store Builder
              </h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#FF6C6C] bg-rose-50 rounded-full shrink-0">
                <LayoutTemplate className="w-3 h-3" />
                Workspace
              </span>
              {hasUnsavedChanges ? (
                <span className="hidden px-2 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 rounded-full sm:inline">
                  Unsaved
                </span>
              ) : null}
            </div>
            <p className="text-[11px] text-gray-500 truncate sm:text-xs">
              {user?.brand_name || "Your brand"} · live preview
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <span className="hidden lg:inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-100 rounded-lg">
              <span className="font-bold text-gray-900">
                {storeConfig.banners?.filter((b) => b.image).length || 0}
              </span>
              banners
            </span>
            <span className="hidden lg:inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-gray-600 bg-gray-50 border border-gray-100 rounded-lg">
              <span className="font-bold text-gray-900">{activeDealCount}</span>
              deals
            </span>
            <span
              className={`hidden lg:inline-flex px-2 py-1 text-[11px] font-semibold rounded-lg ${
                storeConfig.isPublished
                  ? "text-emerald-700 bg-emerald-50"
                  : "text-amber-700 bg-amber-50"
              }`}
            >
              {storeConfig.isPublished ? "Live" : "Draft"}
            </span>

            {storeUrl ? (
              <a
                href={storeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 p-2 text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 sm:px-3 sm:py-1.5 sm:text-xs sm:font-medium"
                title="View live URL"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Live URL</span>
              </a>
            ) : null}

            <div className="hidden w-px h-8 bg-gray-200 sm:block" />

            <button
              type="button"
              onClick={handleBackToDashboard}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-white rounded-xl bg-gradient-to-r from-[#FF6C6C] to-[#FF8585] shadow-md shadow-rose-200/80 ring-2 ring-[#FF6C6C]/20 hover:from-[#e65e5e] hover:to-[#FF6C6C] hover:shadow-lg transition-all sm:px-4 sm:py-2.5 sm:text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Dashboard</span>
            </button>
          </div>
        </div>

        {/* Mobile tab switcher */}
        <div className="px-4 pb-3 lg:hidden">
          <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
            <button
              type="button"
              onClick={() => setMobileTab("edit")}
              className={`inline-flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg ${
                mobileTab === "edit" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
              }`}
            >
              <PencilLine className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setMobileTab("preview")}
              className={`inline-flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg ${
                mobileTab === "preview" ? "bg-white shadow-sm text-gray-900" : "text-gray-500"
              }`}
            >
              <Store className="w-4 h-4" />
              Preview
            </button>
          </div>
        </div>
      </header>

      {/* Builder workspace */}
      <div className="flex-1 min-h-0 px-4 py-4 sm:px-6">
        <div className="grid h-full min-h-0 gap-4 lg:grid-cols-[minmax(360px,440px)_minmax(0,1fr)] xl:grid-cols-[460px_minmax(0,1fr)]">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`min-h-0 overflow-y-auto ${
              mobileTab === "edit" ? "block" : "hidden lg:block"
            }`}
          >
            <StoreEditorPanel
              storeConfig={storeConfig}
              onChange={setStoreConfig}
              onSave={handleSave}
              isSaving={isSaving}
              hasUnsavedChanges={hasUnsavedChanges}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.04 }}
            className={`min-h-0 ${
              mobileTab === "preview" ? "block h-full" : "hidden lg:block lg:h-full"
            }`}
          >
            <DeviceFrame
              ref={previewScrollRef}
              mode={previewMode}
              onModeChange={setPreviewMode}
              scrollToTopToken={previewScrollToken}
            >
              <StorePreview
                storeConfig={storeConfig}
                vendor={user}
                deals={deals}
                previewMode={previewMode}
              />
            </DeviceFrame>
          </motion.div>
        </div>
      </div>

      <UnsavedChangesModal
        isOpen={leaveModalOpen}
        onStay={handleStayOnPage}
        onLeave={handleLeaveAnyway}
        onSave={handleSaveAndLeave}
        isSaving={isSaving}
        description="Information you've entered may not be saved. Are you sure you want to leave this page?"
      />
    </div>
  );
};

export default StoreBuilder;
